import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import travelObject from '@/entities/travel/interface'

import {
  ALL_TRAVEL_SELECTION_BLOCKS_QUERY,
  SINGLE_TRAVEL_SELECTION_BLOCK_QUERY,
  CREATE_TRAVEL_SELECTION_BLOCK_MUTATION,
  UPDATE_TRAVEL_SELECTION_BLOCK_MUTATION,
  DELETE_TRAVEL_SELECTION_BLOCK_MUTATION,
  FORCE_DELETE_TRAVEL_SELECTION_BLOCK_MUTATION,
  ADD_TRAVEL_SELECTION_BLOCK_TRAVELS_MUTATION,
  REMOVE_TRAVEL_SELECTION_BLOCK_TRAVELS_MUTATION,
  CREATE_TRAVEL_SELECTION_BLOCK_TRAVELS_MUTATION,
  DELETE_TRAVEL_SELECTION_BLOCK_TRAVELS_MUTATION,
  UPDATE_TRAVEL_SELECTION_BLOCK_TRAVELS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsTravelSelectionBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_TRAVEL_SELECTION_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.travelSelectionBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_TRAVEL_SELECTION_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.travelSelectionBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_TRAVEL_SELECTION_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.travelSelectionBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_TRAVEL_SELECTION_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.travelSelectionBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_TRAVEL_SELECTION_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travelSelectionBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_TRAVEL_SELECTION_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travelSelectionBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_TRAVEL_SELECTION_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsTravelSelectionBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_TRAVEL_SELECTION_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsTravelSelectionBlock
    })
  },

  assignTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_SELECTION_BLOCK_TRAVELS_MUTATION, variables)
  },

  unassignTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_SELECTION_BLOCK_TRAVELS_MUTATION, variables)
  },

  createTravels: async (id: string, items: Array<travelObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travels: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_SELECTION_BLOCK_TRAVELS_MUTATION, variables)
  },

  deleteTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_SELECTION_BLOCK_TRAVELS_MUTATION, variables)
  },

  updateTravelsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_SELECTION_BLOCK_TRAVELS_ORDER_MUTATION, variables)
  },
}

export default cmsTravelSelectionBlockRepository
